import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import { Save as SaveIcon, Restore as RestoreIcon, Settings as SettingsIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import ConfirmDialog from '../ConfirmDialog';

export default function SettingsTab() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Fetch sync status
  const {
    data: syncStatusResponse,
    isLoading: syncStatusLoading
  } = useQuery({
    queryKey: ['pricingSyncStatus', 'pricingSyncSettingsStatus'],
    queryFn: async () => {
      const response = await api.get('/pricing-sync/status');
      return response.data;
    },
    refetchOnWindowFocus: true,
    staleTime: 0,
    refetchInterval: 5000 // Poll every 5 seconds
  });

  const isSyncRunning = syncStatusResponse?.data?.isRunning || false;

  // State for settings form
  const [settings, setSettings] = useState({
    get_pricing_endpoint: '',
    get_product_variant_price_endpoint: '',
    query_batch_size: '',
    whole_pricing_sync_query: '',
    updated_pricing_sync_query: '',
    pricing_columns: ''
  });

  // State for reset confirmation dialog
  const [showResetDialog, setShowResetDialog] = useState(false);

  // Fetch settings
  const {
    data: settingsData,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['pricingSyncSettings'],
    queryFn: async () => {
      const response = await api.getPricingSyncSettings();
      return response.data.data;
    },
    staleTime: 0
  });

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: async (updatedSettings) => {
      const response = await api.updatePricingSyncSettings(updatedSettings);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Price sync settings updated successfully');
      queryClient.invalidateQueries({ queryKey: ['pricingSyncSettings'] });
    },
    onError: (error) => {
      toast.error(`Failed to update settings: ${error.response?.data?.message || error.message}`);
    }
  });

  // Reset settings mutation
  const resetSettingsMutation = useMutation({
    mutationFn: async () => {
      const response = await api.resetPricingSyncSettings();
      return response.data;
    },
    onSuccess: () => {
      toast.success('Price sync settings reset to default successfully');
      queryClient.invalidateQueries({ queryKey: ['pricingSyncSettings'] });
      setShowResetDialog(false);
    },
    onError: (error) => {
      toast.error(`Failed to reset settings: ${error.response?.data?.message || error.message}`);
      setShowResetDialog(false);
    }
  });

  // Initialize form state when data is loaded
  useEffect(() => {
    if (settingsData) {
      const pricingSyncSettings = settingsData;

      // Format column array as string if it's an array
      let columnsStr = pricingSyncSettings.pricing_columns;
      if (Array.isArray(columnsStr)) {
        columnsStr = columnsStr.join(', ');
      }

      const processedSettings = {
        query_batch_size: pricingSyncSettings.query_batch_size,
        whole_pricing_sync_query: pricingSyncSettings.whole_pricing_sync_query,
        updated_pricing_sync_query: pricingSyncSettings.updated_pricing_sync_query,
        get_pricing_endpoint: pricingSyncSettings.get_pricing_endpoint,
        get_product_variant_price_endpoint: pricingSyncSettings.get_product_variant_price_endpoint,
        pricing_columns: pricingSyncSettings.pricing_columns
      };

      setSettings(processedSettings);
    }
  }, [settingsData]);

  // Handle form input changes
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submission
  const handleSubmit = (event) => {
    event.preventDefault();

    // Format columns string as array
    let columnsArray = settings.pricing_columns;
    if (typeof columnsArray === 'string' && columnsArray.trim() !== '') {
      columnsArray = columnsArray.split(',').map(col => col.trim());
    }

    const updatedSettings = {
      ...settings,
      query_batch_size: parseInt(settings.query_batch_size, 10),
      pricing_columns: columnsArray
    };

    updateSettingsMutation.mutate(updatedSettings);
  };

  // Handle reset settings
  const handleResetSettings = () => {
    setShowResetDialog(true);
  };

  const handleConfirmReset = () => {
    resetSettingsMutation.mutate();
  };

  if (isLoading || syncStatusLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity='error' sx={{ mb: 3 }}>
        Error loading settings: {error.message}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Sync in progress warning banner */}
      {isSyncRunning && isAdmin && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          Pricing synchronization is currently in progress. Settings updates are disabled until the sync completes.
        </Alert>
      )}
      <Paper sx={{ p: '15px' }}>
        <Box display='flex' justifyContent='space-between' alignItems='center' mb={4}>
          <Box display='flex' alignItems='center'>
            <SettingsIcon color='primary' sx={{ mr: 1 }} />
            <Typography variant='h6'>
              Pricing Sync Settings
            </Typography>
          </Box>
          {isAdmin ? (
            <>
              <Button
                variant='contained'
                color='warning'
                onClick={handleResetSettings}
                disabled={!isAdmin || resetSettingsMutation.isPending || isSyncRunning}
                startIcon={<RestoreIcon />}
              >
                Reset
              </Button>
            </>
          ) : (<></>)}
        </Box>

        {!settingsData ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
            <CircularProgress size={40} />
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Price API Endpoint'
                  name='get_pricing_endpoint'
                  value={settings.get_pricing_endpoint}
                  onChange={handleInputChange}
                  disabled={!isAdmin || isSyncRunning}
                  helperText='The endpoint used for reading price data from ERP'
                  variant='outlined'
                  sx={{ mb: 2 }}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Product Variant Price API Endpoint'
                  name='get_product_variant_price_endpoint'
                  value={settings.get_product_variant_price_endpoint}
                  onChange={handleInputChange}
                  disabled={!isAdmin || isSyncRunning}
                  helperText='The endpoint used for reading price data from ERP'
                  variant='outlined'
                  sx={{ mb: 2 }}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Price Columns'
                  name='pricing_columns'
                  value={settings.pricing_columns}
                  onChange={handleInputChange}
                  disabled={!isAdmin || isSyncRunning}
                  helperText='Comma-separated list of columns to fetch from ERP'
                  variant='outlined'
                  sx={{ mb: 2 }}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Price Sync Query Batch Size'
                  name='query_batch_size'
                  type='number'
                  value={settings.query_batch_size}
                  onChange={handleInputChange}
                  disabled={!isAdmin || isSyncRunning}
                  helperText='Number of items to process in each batch'
                  variant='outlined'
                  sx={{ mb: 2 }}
                  InputProps={{
                    endAdornment: <InputAdornment position='end'>items</InputAdornment>
                  }}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label='Whole Price Sync Query'
                  name='whole_pricing_sync_query'
                  value={settings.whole_pricing_sync_query}
                  onChange={handleInputChange}
                  disabled={!isAdmin || isSyncRunning}
                  helperText='Query used to fetch all pricing data from ERP. Use ${company} as a placeholder for the company ID.'
                  variant='outlined'
                  sx={{ mb: 2 }}
                  multiline
                  rows={4}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label='Updated Price Sync Query'
                  name='updated_pricing_sync_query'
                  value={settings.updated_pricing_sync_query}
                  onChange={handleInputChange}
                  disabled={!isAdmin || isSyncRunning}
                  helperText="Query used to fetch today's updated price data from ERP. Use ${company} as a placeholder for the company ID."
                  variant='outlined'
                  sx={{ mb: 2 }}
                  multiline
                  rows={4}
                  required
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              {isAdmin ? (
                <>
                  <Button
                    type='submit'
                    variant='contained'
                    color='primary'
                    startIcon={updateSettingsMutation.isPending ? <CircularProgress size={20} color='inherit' /> : <SaveIcon />}
                    disabled={!isAdmin || updateSettingsMutation.isPending || isSyncRunning}
                  >
                    {updateSettingsMutation.isPending ? 'Saving...' : 'Save Settings'}
                  </Button>
                </>
              ) : (<></>)}
            </Box>
          </form>
        )}
      </Paper>

      <ConfirmDialog
        open={showResetDialog}
        onClose={() => setShowResetDialog(false)}
        onCancel={() => setShowResetDialog(false)}
        onConfirm={handleConfirmReset}
        confirmColor='warning'
        title='Reset Settings to Default'
        message='Are you sure you want to reset all price sync settings to their default values? This action cannot be undone.'
        confirmText='Reset'
        cancelText='Cancel'
        severity='warning'
        isLoading={resetSettingsMutation.isPending}
      />
    </Box>
  );
}
